import Collection from '@/store/models/collections/Collection'
import CollectionMember from '@/store/models/collections/CollectionMember'
import { MemberRole } from '@/utils/permissions/roles'

// Walks the Collection store to collect every descendant collection id of a root.
// Used to scope local-store reconciliation to the sub-collection tree of interest.
function getDescendantCollectionIds(rootId) {
  const ids = new Set()
  const stack = [rootId]
  while (stack.length) {
    const currentId = stack.pop()
    const children = Collection.query().where('parent', currentId).get()
    for (const child of children) {
      if (!ids.has(child._id)) {
        ids.add(child._id)
        stack.push(child._id)
      }
    }
  }
  return ids
}

export default {
  // members
  async getCollectionMembers(_, { collectionId, filters }) {
    // On a fresh fetch (no pagination cursor), purge locally-cached members for
    // this collection so records invalidated remotely (e.g. sub-collection
    // access revoked via EditMemberRole) don't linger — insertOrUpdate alone
    // never removes missing entries.
    const isFreshFetch = !filters?.next
    if (isFreshFetch) {
      await CollectionMember.delete(m => m.collectionId === collectionId)
    }

    const { results, next } = await this.$api.collection.members(collectionId, { params: filters })
    await CollectionMember.insertOrUpdate({ data: results })

    return { results, next }
  },
  async getCollectionMember(_, { collectionId, memberId }) {
    const member = await this.$api.collection.getMember(collectionId, memberId)
    await CollectionMember.insertOrUpdate({ data: member })
    return member
  },
  // join
  async joinViaShareLink(_, { collectionId, token }) {
    return this.$api.collection.acceptShareLink(collectionId, { token })
  },
  async acceptEmailInvite(_, { collectionId, token }) {
    return this.$api.collection.acceptInvite(collectionId, { token })
  },
  async getShareLinkInfo(_, { collectionId, token }) {
    return this.$api.collection.shareLinkInfo(collectionId, { token })
  },
  async getInviteInfo(_, { collectionId, token }) {
    return this.$api.collection.inviteInfo(collectionId, { token })
  },
  // update member role + sub-collection access
  async updateMember(_, { collectionId, memberId, role, subCollectionIds }) {
    await this.$api.collection.updateMember(collectionId, memberId, { role, subCollectionIds })

    const targetUserId = CollectionMember.find(memberId)?.userId

    // Update local store
    await CollectionMember.update({
      where: memberId,
      data: { role, isOwner: role === MemberRole.ADMIN },
    })

    // Reconcile sub-collection memberships locally so revoked access stops
    // rendering in sub-collection avatar groups / lists immediately. A full
    // refetch of every sub-collection is too expensive and may never happen
    // (e.g. keep-alive parent routes), so we purge the stale records eagerly.
    // Admins get access to every sub-collection regardless of the submitted
    // list (backend ignores subCollectionIds for ADMIN), so skip revocation.
    if (targetUserId && Array.isArray(subCollectionIds) && role !== MemberRole.ADMIN) {
      const descendantIds = getDescendantCollectionIds(collectionId)
      const grantedIds = new Set(subCollectionIds)

      await CollectionMember.delete(m =>
        m.userId === targetUserId
        && descendantIds.has(m.collectionId)
        && !grantedIds.has(m.collectionId),
      )
    }
  },
  // leave
  async removeMember(_, { collectionId, memberId }) {
    await this.$api.collection.removeMember(collectionId, memberId)
    await CollectionMember.delete(memberId)
  },
  // leave collection (self-removal) - finds the user's own membership and removes it
  async leaveCollection({ rootGetters }, { collectionId }) {
    const userId = rootGetters['auth/userId']
    // Try to find membership from the store. The backend guarantees the current
    // user's membership is included in every collection's membersPreview, so the
    // local store is populated whenever the (sub-)collection is visible.
    let membership = CollectionMember.query()
      .where('collectionId', collectionId)
      .where('userId', userId)
      .first()

    if (!membership) {
      // Fallback: paginate the members endpoint until our membership is found.
      // The endpoint only allows limit ∈ [5, 10, 25]; use the max to minimize round trips.
      let next
      do {
        const params = { limit: 25, ...(next ? { next } : {}) }
        const page = await this.$api.collection.members(collectionId, { params })
        await CollectionMember.insertOrUpdate({ data: page.results })
        membership = page.results.find(m => (m.userId || m.user?._id || m.user) === userId)
        next = page.next
      } while (!membership && next)
    }

    if (!membership) {
      throw new Error('Membership not found')
    }

    const memberId = membership._id || membership.id
    await this.$api.collection.removeMember(collectionId, memberId)
    await CollectionMember.delete(memberId)
  },
}
